import {
  AnimationConfigWithData,
  AnimationConfigWithPath,
  AnimationItem,
  LottiePlayer,
} from 'lottie-web/build/player/lottie_light';
import type { FunctionComponent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import LottieController from '../../lazyLibraries/LottieController';
import { useIsLargerThanWide } from '../../styles/hooks/media';

interface LobbyButtonAnimationProps {
  animation: Partial<AnimationConfigWithPath<'svg'> | AnimationConfigWithData<'svg'>>;
  canPlay: boolean;
  className: string;
  complete: boolean;
  fallback: ReactNode;
  onComplete?: () => void;
}

export const LobbyButtonAnimation: FunctionComponent<LobbyButtonAnimationProps> = ({
  animation,
  canPlay,
  className,
  complete,
  fallback,
  onComplete,
}) => {
  // TODO: this component should not render if is wide is true
  const isWide = useIsLargerThanWide();

  useEffect(() => {
    if (!complete && !isWide) {
      LottieController.init();
    }
  }, [isWide, complete]);

  // Animation container ref
  const animationRef = useRef<HTMLSpanElement>(null);
  // Animation ref
  const lottieRef = useRef<AnimationItem>();
  // Animation complete boolean value
  const [completeInternal, setCompleteInternal] = useState(false);

  const [lottie, setLottie] = useState<LottiePlayer>();

  // Set complete initially from props
  useEffect(() => {
    setCompleteInternal(complete);
  }, [complete]);

  useEffect(() => {
    LottieController.addListener('lobbyAnimation', (lottie) => {
      setLottie(lottie);
    });
  });

  // fire callback onComplete function when animation completed and update internal state
  const handleComplete = useCallback((): void => {
    // Avoid blinking, if animation not finished ye t
    setTimeout(() => {
      setCompleteInternal(true);
    }, 1000);
    if (onComplete) {
      onComplete();
    }
  }, [onComplete]);

  useEffect(() => {
    if (animationRef.current) {
      lottieRef.current = lottie?.loadAnimation({
        ...animation,
        container: animationRef.current, // the dom element that will contain the animation
      });
    }
  }, [animation, lottie]);

  useEffect(() => {
    // add complete listener to animation
    if (lottieRef.current) {
      lottieRef.current.addEventListener('complete', () => handleComplete());
    }

    return () => {
      lottieRef.current?.removeEventListener('complete', () => handleComplete());
    };
  }, [handleComplete, lottieRef.current]);

  useEffect(() => {
    // play if animation loaded
    if (canPlay && lottieRef.current && !completeInternal) {
      lottieRef.current.play();
    }
    // If fallback rendered make animation complete to not play this animation in future
    if (!lottie && canPlay) {
      handleComplete();
    }
  }, [canPlay, completeInternal, handleComplete, lottie]);

  // conditionally render animation or fallback
  return lottie && !completeInternal ? <span ref={animationRef} className={className} /> : fallback;
};
